import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './MenuCategoriesSlider.css';
import { getSanityImageProps } from '../../utils/sanityImage';

// Helper function to extract plain text from portable text
const extractPlainText = (portableText) => {
  if (!portableText) return '';
  if (typeof portableText === 'string') return portableText;
  if (Array.isArray(portableText)) {
    return portableText
      .map(block => {
        if (block._type === 'block' && block.children) {
          return block.children.map(child => child.text).join('');
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

export default function MenuCategoriesSlider({ 
  categories = [], 
  loading = false,
  title = "OUR HAND-CRAFTED FAVORITES",
  subtitle = "From sweet to savory, our menu features hand-crafted tastes for everyone."
}) {
  // Use mock data if no categories are provided or while loading
  const displayCategories = categories.length > 0 ? categories : (loading ? [] : mockCategories);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef(null);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);

  // Calculate slider dimensions on mount and window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (sliderRef.current) {
        // Get container width
        const containerWidth = sliderRef.current.clientWidth;
        
        // For mobile (under 768px), show 1.2 cards
        // For desktop, show 4.2 cards
        const isMobile = window.innerWidth < 768;
        const visibleCards = isMobile ? 1.2 : 4.2;
        const visibleFullCards = isMobile ? 1 : 4;
        
        const estimatedItemWidth = containerWidth / visibleCards;
        
        setSliderWidth(containerWidth);
        setItemWidth(estimatedItemWidth);
        
        // Calculate max index (total items - visible items)
        setMaxIndex(Math.max(0, displayCategories.length - visibleFullCards));
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [displayCategories.length]);

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  // Handle drag end to snap to nearest slide
  const handleDragEnd = (event, info) => {
    const threshold = 50; // Minimum drag distance to trigger slide change
    const velocity = info.velocity.x;
    const offset = info.offset.x;

    // Determine direction and magnitude of drag
    if (Math.abs(offset) > threshold || Math.abs(velocity) > 500) {
      if (offset > 0 || velocity > 500) {
        // Dragged right or high velocity right - go to previous
        handlePrev();
      } else {
        // Dragged left or high velocity left - go to next
        handleNext();
      }
    }
  };

  return (
    <section className="menu-categories-section overflow-hidden py-16">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Desktop header layout */}
        <div className="hidden sm:block mb-8 relative z-10">
          <div className="menu-categories-nav mb-4">
            <button 
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="text-white hover:text-brand-cream disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
              className="text-white hover:text-brand-cream disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div>
            <h2 className="text-4xl md:text-5xl font-heading text-white mb-2">{title}</h2>
            <p className="text-white text-lg max-w-2xl">
              {subtitle}
            </p>
          </div>
        </div>
        
        {/* Mobile header layout with arrows under description */}
        <div className="sm:hidden mb-6 relative z-10">
          <h2 className="text-3xl font-heading text-white mb-2">{title}</h2>
          <p className="text-white text-base max-w-2xl mb-4">
            {subtitle}
          </p>
          <div className="menu-categories-nav">
            <button 
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="text-white hover:text-brand-cream disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
              className="text-white hover:text-brand-cream disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Netflix-style slider with full bleed on the right */}
      <div className="menu-categories-slider" ref={sliderRef}>
        <motion.div 
          className="menu-categories-slider-track"
          drag="x"
          dragConstraints={{ 
            left: -maxIndex * (itemWidth || 0), 
            right: 0 
          }}
          dragElastic={0.2}
          dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
          onDragEnd={handleDragEnd}
          animate={{ x: -currentIndex * (itemWidth || 0) }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          style={{ cursor: 'grab' }}
          whileTap={{ cursor: 'grabbing' }}
        >
          {loading ? (
            <div className="flex items-center justify-center w-full py-20">
              <p className="text-white text-xl">Loading menu categories...</p>
            </div>
          ) : displayCategories.map((category, index) => (
            <div 
              key={category._id} 
              className="menu-categories-slider-item"
              style={{ width: itemWidth || '350px' }}
            >
              {/* Alternating layout based on index */}
              {index % 2 === 0 ? (
                // Even items: Image on top, text on bottom
                <Link 
                  to={`/menu/${category.slug}`}
                  className="menu-category-card relative z-10 block group"
                >
                  <div className="menu-category-image menu-category-image-top">
                    <img 
                      {...getSanityImageProps(category.imageUrl, 'menuCard')}
                      alt={category.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-brand-navy opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading text-brand-navy mb-1 px-2">{category.name}</h3>
                    <p className="menu-category-description text-brand-navy px-2">{extractPlainText(category.description)}</p>
                  </div>
                </Link>
              ) : (
                // Odd items: Text on top, image on bottom
                <Link 
                  to={`/menu/${category.slug}`}
                  className="menu-category-card relative z-10 block group"
                >
                  <div>
                    <h3 className="text-2xl font-heading text-brand-cream mb-1 px-2">{category.name}</h3>
                    <p className="menu-category-description text-white px-2">{extractPlainText(category.description)}</p>
                  </div>
                  <div className="menu-category-image menu-category-image-bottom">
                    <img 
                      {...getSanityImageProps(category.imageUrl, 'menuCard')}
                      alt={category.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-brand-navy opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </div>
                </Link>
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
